import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllHotels, getProperties } from '../../services/g2TravelApi';
import {
  HOTEL_SECTION_PLACEHOLDER_SMALL,
  resolveHotelSectionImage,
} from '../../lib/hotelSectionImages';
import { buildHotelDetailPath } from '../../lib/hotelRoutes';
import './PropertyTypeCarousel.css';

const PropertyTypeCarousel = ({ onViewHotel }) => {
  const navigate = useNavigate();
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const carouselRef = useRef(null);

  // Update mobile state on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchHotels = async () => {
      setLoading(true);
      try {
        console.log('🔍 Fetching hotels for property type carousel...');

        let baseHotels = [];

        // Try full hotels endpoint first
        try {
          const allHotelsResponse = await getAllHotels({});
          if (allHotelsResponse?.success && Array.isArray(allHotelsResponse.hotels) && allHotelsResponse.hotels.length > 0) {
            baseHotels = allHotelsResponse.hotels;
          }
        } catch (err) {
          console.warn('⚠️ getAllHotels failed, falling back to getProperties:', err);
        }

        // Fallback to static properties list if needed
        if (!baseHotels.length) {
          const propertiesData = await getProperties();
          if (Array.isArray(propertiesData) && propertiesData.length > 0) {
            baseHotels = propertiesData;
          }
        }

        if (!Array.isArray(baseHotels) || baseHotels.length === 0) {
          console.warn('⚠️ No hotels found from G2 Travel API');
          setHotels([]);
          return;
        }

        // Take first 8 hotels for the carousel
        const selectedHotels = baseHotels.slice(0, 8);

        // Prepare images for each hotel (no external API)
        console.log('📸 Preparing images for property type carousel...');
        const hotelsWithImages = selectedHotels.map((hotel, index) => {
          const hotelName = hotel.name || hotel.propertyName || hotel.hotelName || 'Hotel';
          const imageUrl = resolveHotelSectionImage(hotel, index, 'small');

          return {
            id: hotel.id || hotel.propertyId || hotel.code || `hotel-${index}`,
            name: hotelName,
            image: imageUrl,
            city: hotel.cityName || hotel.city || hotel.location || '',
            country: hotel.countryName || hotel.country || 'United Kingdom',
            hotelData: hotel,
          };
        });

        console.log(`✅ Loaded ${hotelsWithImages.length} hotels with images for carousel`);
        setHotels(hotelsWithImages);
      } catch (error) {
        console.error('❌ Error fetching hotels for carousel:', error);
        setHotels([]);
      } finally {
        setLoading(false);
      }
    };

    fetchHotels();
  }, []);

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    const maxIndex = Math.max(0, hotels.length - 4);
    if (currentIndex < maxIndex) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleHotelClick = (hotel) => {
    if (onViewHotel && hotel.id) {
      onViewHotel(hotel.id, hotel.hotelData);
    } else {
      navigate(buildHotelDetailPath(hotel.id));
    }
  };

  // On mobile, show all hotels for horizontal scrolling
  const visibleHotels = isMobile ? hotels : hotels.slice(currentIndex, currentIndex + 4);
  const canGoPrev = !isMobile && currentIndex > 0;
  const canGoNext = !isMobile && currentIndex < hotels.length - 4;

  if (loading) {
    return (
      <section className="property-type-carousel">
        <div className="property-type-carousel__container">
          <h2 className="property-type-carousel__title">Browse by Hotels</h2>
          <div className="property-type-carousel__loading">Loading hotels...</div>
        </div>
      </section>
    );
  }

  if (hotels.length === 0) {
    return null;
  }

  return (
    <section className="property-type-carousel">
      <div className="property-type-carousel__container">
        <h2 className="property-type-carousel__title">Browse by property type</h2>
        
        <div className="property-type-carousel__wrapper" ref={carouselRef}>
          <button 
            className={`property-type-carousel__arrow property-type-carousel__arrow--left ${!canGoPrev ? 'property-type-carousel__arrow--disabled' : ''}`}
            onClick={handlePrev}
            disabled={!canGoPrev}
            aria-label="Previous hotels"
          >
            ‹
          </button>
          
          <div className="property-type-carousel__cards">
            {visibleHotels.map((hotel) => (
              <div
                key={hotel.id}
                className="property-type-carousel__card"
                onClick={() => handleHotelClick(hotel)}
              >
                <div className="property-type-carousel__card-image-wrapper">
                  <img
                    src={hotel.image}
                    alt={hotel.name}
                    className="property-type-carousel__card-image"
                    onError={(e) => {
                      e.target.src = HOTEL_SECTION_PLACEHOLDER_SMALL;
                    }}
                  />
                </div>
                <h3 className="property-type-carousel__card-name">{hotel.name}</h3>
              </div>
            ))}
          </div>

          <button
            className={`property-type-carousel__arrow property-type-carousel__arrow--right ${!canGoNext ? 'property-type-carousel__arrow--disabled' : ''}`}
            onClick={handleNext}
            disabled={!canGoNext}
            aria-label="Next hotels"
          >
            ›
          </button>
        </div>
      </div>
    </section>
  );
};

export default PropertyTypeCarousel;
